/**
 * Merging two copies of one trip, section by section.
 *
 * A trip syncs as one record, but people edit its sections on different devices: a phone adds
 * to section 2's basket offline while a laptop plans section 3. Whole-record last-writer-wins
 * would drop one of those. Instead the worker and every device merge the two copies:
 *
 *   sections        by id; the copy with the later `updated_at` wins, a tie goes to the greater
 *                   canonical JSON (as the worker breaks record ties)
 *   removals        `removed_sections` (id -> when) is unioned; a section removed at or after
 *                   its own last edit stays gone, one edited after its removal comes back
 *   trip fields     name, region, notes and section order come from the copy whose
 *                   `info_updated_at` is later (falling back to `updated_at`)
 *   updated_at      the later of the two
 *
 * The merge is commutative, associative and idempotent, so devices converge whatever order
 * copies arrive in. Pure: no clocks; `restampTrip` takes the stamp as an argument.
 */
import { timeOf } from './time'
import type { IsoTime, Section, Trip } from './types'

/** JSON with object keys sorted at every depth; arrays keep their order. */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null'
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`
  const obj = value as Record<string, unknown>
  const parts: string[] = []
  for (const key of Object.keys(obj).sort()) {
    if (obj[key] === undefined) continue
    parts.push(`${JSON.stringify(key)}:${canonicalJson(obj[key])}`)
  }
  return `{${parts.join(',')}}`
}

/** The fields a merge takes whole from one side, with the section order. */
function info(t: Trip): unknown {
  const { sections, removed_sections: _r, updated_at: _u, info_updated_at: _i, ...rest } = t
  return { ...rest, order: sections.map((s) => s.id) }
}

/** Whether `a` wins over `b` by stamp, a tie going to the greater canonical JSON. */
function wins(ta: number, tb: number, a: unknown, b: unknown): boolean {
  if (ta !== tb) return ta > tb
  return canonicalJson(a) >= canonicalJson(b)
}

function later(a: IsoTime | undefined, b: IsoTime | undefined): IsoTime | undefined {
  if (a === undefined) return b
  if (b === undefined) return a
  return timeOf(a) >= timeOf(b) ? a : b
}

/** Both copies must be live trips with the same id; tombstones go by plain LWW. */
export function mergeTrips(a: Trip, b: Trip): Trip {
  const infoA = timeOf(a.info_updated_at ?? a.updated_at)
  const infoB = timeOf(b.info_updated_at ?? b.updated_at)
  const [lead, other] = wins(infoA, infoB, info(a), info(b)) ? [a, b] : [b, a]

  const removed: Record<string, IsoTime> = { ...a.removed_sections }
  for (const [id, at] of Object.entries(b.removed_sections ?? {})) {
    removed[id] = later(removed[id], at) as IsoTime
  }

  const pick = new Map<string, Section>()
  for (const s of [...a.sections, ...b.sections]) {
    const seen = pick.get(s.id)
    if (!seen || wins(timeOf(s.updated_at), timeOf(seen.updated_at), s, seen)) pick.set(s.id, s)
  }
  const alive = (s: Section): boolean => {
    const gone = removed[s.id]
    return gone === undefined || timeOf(s.updated_at) > timeOf(gone)
  }

  const sections: Section[] = []
  const placed = new Set<string>()
  for (const s of [...lead.sections, ...other.sections]) {
    if (placed.has(s.id)) continue
    placed.add(s.id)
    const chosen = pick.get(s.id) as Section
    if (alive(chosen)) sections.push(chosen)
  }

  const {
    removed_sections: _r,
    updated_at: _u,
    info_updated_at: _i,
    sections: _s,
    ...fields
  } = lead
  const out: Trip = { ...fields, sections }
  const updated = later(a.updated_at, b.updated_at)
  if (updated !== undefined) out.updated_at = updated
  const infoAt = later(a.info_updated_at, b.info_updated_at)
  if (infoAt !== undefined) out.info_updated_at = infoAt
  // A tombstone only matters while some copy might still hold the section it removed.
  const kept = Object.entries(removed).filter(([id]) => !sections.some((s) => s.id === id))
  if (kept.length > 0) out.removed_sections = Object.fromEntries(kept)
  return out
}

/**
 * Stamp what an edit changed, so a later merge knows which side holds the newer copy: every
 * section added or changed gets `stamp`, a section dropped is recorded in `removed_sections`,
 * and a change to the trip's own fields or section order moves `info_updated_at`.
 */
export function restampTrip(before: Trip, after: Trip, stamp: IsoTime): Trip {
  const old = new Map(before.sections.map((s) => [s.id, canonicalJson(s)]))
  const sections = after.sections.map((s) =>
    old.get(s.id) === canonicalJson(s) ? s : { ...s, updated_at: stamp },
  )
  const removed: Record<string, IsoTime> = { ...after.removed_sections }
  for (const s of sections) delete removed[s.id]
  for (const s of before.sections) {
    if (!sections.some((n) => n.id === s.id)) removed[s.id] = stamp
  }
  const next: Trip = { ...after, sections, updated_at: stamp }
  if (Object.keys(removed).length > 0) next.removed_sections = removed
  else delete next.removed_sections
  if (canonicalJson(info(before)) !== canonicalJson(info(next))) next.info_updated_at = stamp
  return next
}
